<template>
  <div class="contract-left-header">
    <div class="contract-info-amendment" v-if="amendments.length">
      <div class="label ms-help">Версия договора</div>
      <i-select
        v-model="amendment"
        @on-change="changeAmendment"
        not-found-text="ДС отсутствуют"
        size="large"
      >
        <i-option v-for="item in amendments" :value="item.id" :key="item.id">
          <template v-if="item.id !== 'original'">
            ДС №{{ item.number }} от {{ formatDefaultDate(item.date) }}
          </template>
          <template v-else>
            Основной договор
          </template>
        </i-option>
      </i-select>
    </div>
    <collapsed-card icon="md-information-circle" title="Общие сведения">
      <inline-loader :loading="loading" v-if="loading" />
      <div class="contract-info" v-else>
        <div class="contract-info__header">
          <div class="contract-info__number ms-subhead">
            Договор №{{ contract.number }} от
            {{ formatDefaultDate(contract.sign_date) }}
          </div>
          <tag :color="getContractStatusColor(contract.status)">
            {{ contract.status }}
          </tag>
        </div>
        <router-link
          v-if="contract.parent_id"
          :to="`/app/contracts/${contract.parent_id}`"
        >
          <div style="margin-top: 4px" class="ms-link">
            Перейти к родительскому проекту
          </div>
        </router-link>
        <div class="contract-info__costs">
          <tag class="ms-costs" color="primary" size="medium">
            {{ getFormattedCosts(contract.costs, false, contract.with_vat) }}
          </tag>
        </div>
        <div
          v-if="contract.is_original_exists"
          class="contract-info__original ms-success"
        >
          <icon type="md-checkmark-circle-outline" />
          Оригиналы документов получены
        </div>
        <div
          v-if="contract.spec_account"
          class="contract-info__spec-account ms-help"
        >
          <icon type="md-checkmark-circle-outline" />
          Спец. счет ({{ contract.spec_account_bank || 'наименование банка не указано' }})
        </div>
        <div class="contract-info__container">
          <div class="contract-info__code ms-text">
            <span class="ms-help">Шифр объекта:</span> {{ contract.code }}
          </div>
          <div class="contract-info__name ms-text">
            <span class="ms-help">Наименование:</span> {{ contract.name }}
          </div>
          <div class="contract-info__companies">
            <div class="contract-info__company customer">
              <span class="ms-help">Заказчик:</span>
              <contract-prop-label
                type="contragents"
                :label="customerLabel"
                :source="contract.customer_source"
              />
            </div>
            <div class="contract-info__company performer">
              <span class="ms-help">Исполнитель:</span>
              <contract-prop-label
                type="own_companies"
                :id="contract.performer_id"
                :label="contract.performer"
              />
            </div>
          </div>
          <div class="contract-info__dates">
            <div class="contract-info__date start">
              <span class="ms-help"> Начало: </span>
              <span class="ms-text">
                {{ formatDefaultDate(contract.start_date) }}
              </span>
            </div>
            <div class="contract-info__date end">
              <span class="ms-help"> Окончание: </span>
              <span class="ms-text">
                {{ formatDefaultDate(contract.end_date) }}
              </span>
            </div>
          </div>
          <div v-if="contract.contract_type" class="contract-info__type">
            <span class="ms-help">Условие закрытия:</span>
            <span class="ms-text">
              {{ getContractType(contract.contract_type) }}
            </span>
          </div>
          <div class="contract-info__manager" v-if="contract.manager_id">
            <span class="ms-help"> Менеджер проекта: </span>
            <contract-prop-label
              :id="contract.manager_id"
              :label="contract.manager_name"
            />
          </div>
          <div class="contract-info__manager" v-if="contract.field_manager_id">
            <span class="ms-help"> Полевой ГИП: </span>
            <contract-prop-label
              :id="contract.field_manager_id"
              :label="contract.field_manager_name"
            />
          </div>
        </div>
        <div class="contract-info__job-types">
          <div class="contract-info__job-types-title ms-help">Виды работ:</div>
          <tag
            color="default"
            v-for="(type, i) in contract.job_types"
            :key="type + i"
          >
            {{ type }}
          </tag>
        </div>
      </div>

      <dropdown
        slot="dropdown"
        placement="bottom-end"
        trigger="click"
        @on-click="dropdownClickHandler"
        v-if="getContractModulePerms('contract_info', 'update')"
      >
        <i-button type="text" class="collapsed-card-button ms-link">
          Редактировать
        </i-button>
        <dropdown-menu slot="list">
          <dropdown-item class="ms-text" name="contract-info">
            Общие сведения
          </dropdown-item>
          <dropdown-item class="ms-text" name="pay-conditions">
            Условия приемки и оплаты
          </dropdown-item>
          <dropdown-item class="ms-text" name="prepaid"> Аванс </dropdown-item>
          <dropdown-item class="ms-text" name="retention">
            Гарантийное удержание
          </dropdown-item>
        </dropdown-menu>
      </dropdown>

      <modal
        class="contract-edit-modal"
        v-model="editModal"
        width="760"
        :styles="{ top: '40px' }"
        :mask-closable="false"
      >
        <div slot="header" class="law-cases-editor__header">
          <icon size="16" type="md-information-circle" class="ms-help" />
          <span style="margin-left: 4px" class="ms-help">
            {{ modalNames[modalComponent] }}
          </span>
        </div>
        <component
          :is="modalComponent"
          :contract="editingContract"
          :show="editModal"
          v-if="editModal"
        />
        <div slot="footer" class="contract-subinfo-edit">
          <i-button
            type="success"
            size="small"
            :disabled="isSavingDisabled"
            :loading="saving"
            @click="save"
          >
            Сохранить
          </i-button>
        </div>
      </modal>
    </collapsed-card>
  </div>
</template>

<script>
import moment from 'moment'
import cloneDeep from 'lodash.clonedeep'
import CollapsedCard from '@/components/parts/collapsed-card'
import InlineLoader from '@/components/loaders/inline-loader'
import ContractInfo from '@/components/contract-editor/steps/common-info'
import PayConditions from '@/components/contract-editor/steps/pay-conditions'
import Prepaid from '@/components/contract-editor/steps/prepaid'
import Retention from '@/components/contract-editor/steps/retention'
import ContractPropLabel from '../info/contract-prop-label'
import {
  getContractStatusColor,
  getFormattedCosts,
  formatDefaultDate,
  getNewComplexValues,
  parseDateToServer
} from '@/utils'
import { requiredCommonFields } from '@/config/contracts'

const modalNames = {
  'contract-info': 'Редактирование общих сведений',
  'pay-conditions': 'Редактирование условий оплаты',
  prepaid: 'Редактирование аванса',
  retention: 'Редактирование гарантийного удержания'
}

export default {
  data () {
    return {
      loading: false,
      contract: {},
      editingContract: {},
      editModal: false,
      modalComponent: 'contract-info',
      saving: false,
      amendments: [],
      amendment: '',
      initialJobTypes: [],
      initialFileTypes: [],
      modalNames
    }
  },
  components: {
    CollapsedCard,
    InlineLoader,
    ContractInfo,
    PayConditions,
    Prepaid,
    Retention,
    ContractPropLabel
  },
  watch: {
    '$route.query.amendment_id': function (value) {
      this.getData()
    }
  },
  computed: {
    customerLabel () {
      const { customer, customer_group_name } = this.contract

      return `${customer}${
        customer_group_name ? ` (${customer_group_name})` : ''
      }`
    },
    isSavingDisabled () {
      if (this.modalComponent === 'contract-info') {
        if (requiredCommonFields.some(f => !this.editingContract[f])) {
          return true
        }
      }

      return false
    }
  },
  methods: {
    changeAmendment (id) {
      if (!id) return

      this.$router.replace({ query: { amendment_id: id } })
    },
    async getData () {
      const { id } = this.$route.params
      const { amendment_id } = this.$route.query
      this.amendment = ''
      this.amendments = []

      try {
        this.loading = true

        let url = `page/project/${id}`
        if (amendment_id) url += `?amendment_id=${amendment_id || ''}`
        this.contract = await this.$store.dispatch('GET_REQUEST', { url })

        this.$store.commit('SET_CURRENT_CONTRACT', this.contract)

        const config = {
          where: [
            {
              field: 'contract_id',
              op: '=',
              value: id
            }
          ],
          order: ['date']
        }
        const amendmentsUrl = `objectInfo/amendments?config=${JSON.stringify(
          config
        )}`
        const data = await this.$store.dispatch('GET_REQUEST', {
          url: amendmentsUrl
        })
        if (data.length) {
          this.amendments = [
            { id: 'original', name: 'Основной договор' },
            ...data
          ]
          const last = this.amendments[this.amendments.length - 1]
          this.amendment = amendment_id || last.id
        }

        this.loading = false
      } catch (error) {
        console.log(error)
        this.loading = false
      }
    },
    async getEditingData () {
      const { id } = this.$route.params
      const { amendment_id } = this.$route.query
      if (amendment_id && amendment_id !== 'original') {
        this.amendment = amendment_id
      }

      try {
        // // get contract common info
        let url = `page/project/${id}`
        if (this.amendment) url += `?amendment_id=${this.amendment}`
        const project = await this.$store.dispatch('GET_REQUEST', { url })

        this.initialJobTypes = cloneDeep(project.contract_job_types)
        project.contract_job_types = project.contract_job_types.map(
          t => t.job_type_id
        )

        this.initialFileTypes = cloneDeep(project.mandatory_file_types)
        project.mandatory_file_types = project.mandatory_file_types.map(
          t => t.file_type_id
        )

        this.editingContract = project
        if (this.contract.sign_date) {
          this.editingContract.sign_date = formatDefaultDate(
            this.contract.sign_date
          )
        }
        if (this.editingContract.prepaid_condition_date) {
          this.editingContract.prepaid_condition_date = formatDefaultDate(
            this.editingContract.prepaid_condition_date
          )
        }
        if (this.editingContract.retention_condition_date) {
          this.editingContract.retention_condition_date = formatDefaultDate(
            this.editingContract.retention_condition_date
          )
        }

        this.editingContract.payment_tasks = this.editingContract.payment_tasks
          ? this.editingContract.payment_tasks.map(t => ({
            ...t,
            value: Number(t.value),
            payment_date: formatDefaultDate(t.payment_date)
          }))
          : []
        this.editingContract.prepaid_percentage = Number(
          this.editingContract.prepaid_percentage
        )
        this.editingContract.retention_percentage = Number(
          this.editingContract.retention_percentage
        )

        // get contract roadmaps for prepaid and retention
        let roadmapsUrl = `page/roadmap?contract_id=${id}`
        if (this.amendment) roadmapsUrl += `&amendment_id=${this.amendment}`

        const roadmaps = await this.$store.dispatch('GET_REQUEST', {
          url: roadmapsUrl
        })
        this.$set(
          this.editingContract,
          'roadmaps',
          roadmaps.filter(r => !r.is_retention)
        )
      } catch (error) {
        console.log(error)
      }
    },
    async dropdownClickHandler (component) {
      await this.getEditingData()
      this.modalComponent = component
      this.editModal = true
    },
    getSavingData () {
      switch (this.modalComponent) {
        case 'contract-info':
          return this.getCommonInfoData()
        case 'pay-conditions':
          return this.getPayConditionsData()
        case 'prepaid':
          return this.getPrepaidData()
        case 'retention':
          return this.getRetentionData()
      }
    },
    getCommonInfoData () {
      const {
        id,
        name,
        code,
        number,
        performer_id,
        fact_performer_id,
        customer_id,
        sign_date,
        manager_id,
        field_manager_id,
        contract_status_id,
        is_original_exists,
        contract_job_types,
        mandatory_file_types,
        spec_account,
        spec_account_bank
      } = this.editingContract

      const parsedSignDate = parseDateToServer(sign_date)

      return {
        id,
        name,
        code,
        number,
        performer_id,
        fact_performer_id,
        customer_id,
        is_original_exists,
        spec_account,
        spec_account_bank,
        manager_id: manager_id || null,
        field_manager_id: field_manager_id || null,
        contract_status_id,
        sign_date: parsedSignDate,
        contract_job_types: getNewComplexValues(
          contract_job_types,
          this.initialJobTypes,
          'job_type_id'
        ),
        mandatory_file_types: getNewComplexValues(
          mandatory_file_types,
          this.initialFileTypes,
          'file_type_id'
        )
      }
    },
    getPayConditionsData () {
      const {
        id,
        accept_condition_act,
        accept_condition_act_is_kalendar,
        special_condition_finalizing,
        accept_condition_to,
        accept_condition_to_is_kalendar,
        special_condition_to,
        sign_condition_act,
        sign_condition_act_is_kalendar,
        special_condition_sign,
        with_vat,
        contract_type
      } = this.editingContract

      return {
        id,
        accept_condition_act,
        accept_condition_act_is_kalendar,
        special_condition_finalizing,
        accept_condition_to,
        accept_condition_to_is_kalendar,
        special_condition_to,
        sign_condition_act,
        sign_condition_act_is_kalendar,
        special_condition_sign,
        with_vat,
        contract_type
      }
    },
    getPrepaidData () {
      const {
        roadmaps,
        id,
        prepaid,
        prepaid_scenario,
        prepaid_stages,
        prepaid_date_type,
        prepaid_percentage,
        prepaid_amount,
        prepaid_condition,
        prepaid_condition_is_calendar,
        prepaid_condition_date,
        payment_tasks
      } = this.editingContract

      return {
        prepaid_condition_date: prepaid_condition_date
          ? moment(prepaid_condition_date).format('YYYY-MM-DD')
          : null,
        roadmaps,
        id,
        prepaid,
        prepaid_scenario,
        prepaid_stages,
        prepaid_date_type,
        prepaid_percentage,
        prepaid_amount,
        prepaid_condition,
        prepaid_condition_is_calendar,
        payment_tasks
      }
    },
    getRetentionData () {
      const {
        roadmaps,
        id,
        retention,
        retention_scenario,
        retention_stages,
        retention_date_type,
        retention_percentage,
        retention_amount,
        retention_condition,
        retention_condition_is_calendar,
        retention_condition_date
      } = this.editingContract

      return {
        retention_condition_date: retention_condition_date
          ? moment(retention_condition_date).format('YYYY-MM-DD')
          : null,
        roadmaps,
        id,
        retention,
        retention_scenario,
        retention_stages,
        retention_date_type,
        retention_percentage,
        retention_amount,
        retention_condition,
        retention_condition_is_calendar
      }
    },
    async save () {
      const { amendment_id } = this.$route.query

      try {
        this.saving = true
        let url = `page/project`
        if (amendment_id) url += `?amendment_id=${amendment_id}`
        const data = this.getSavingData()

        await this.$store.dispatch('POST_REQUEST', { url, data })
        this.$emit('update')
        await this.getData()
        this.$Message.success('Сохранение данных выполнено успешно')
        this.editModal = false
        this.saving = false
      } catch (error) {
        console.log(error)
        const message =
          error.response.data[0] || 'Произошла ошибка при сохранении объекта'
        this.$Message.error(message)
        this.saving = false
      }
    },
    getContractType (type) {
      switch (type) {
        case 'final':
          return 'Исполнительная смета'
        default:
          return 'Твердая цена'
      }
    },
    getContractStatusColor,
    getFormattedCosts,
    formatDefaultDate
  },
  created () {
    this.getData()

    const { amendment_id } = this.$route.query
    if (amendment_id && amendment_id !== 'original') {
      this.amendment = amendment_id
    }
  }
}
</script>

<style lang="less">
.contract-info {
  &__container {
    padding: 8px;
    background-color: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid #e8eaec !important;
    border-radius: 4px;
    margin-bottom: 8px;
  }

  &-amendment {
    margin-bottom: 16px;

    .label {
      font-size: 12px;
    }

    .ivu-select {
      width: 100%;

      &-selected-value {
        font-size: 14px !important;
      }
    }
  }

  &__original {
    margin-top: 4px;
    margin-bottom: 4px;
    display: flex;
    align-items: center;

    i {
      margin-right: 4px;
    }
  }

  &__spec-account {
    margin-bottom: 8px;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;

    .ivu-tag {
      flex-shrink: 0;
    }
  }

  &__number {
    margin-right: 8px;
  }

  &__name {
    word-break: break-all;
  }

  &__company {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__manager {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__costs {
    margin: 8px 0;
  }

  &__type {
    .ms-help {
      margin-right: 4px;
    }
  }

  &__dates {
    display: flex;
  }

  &__date {
    &.end {
      margin-left: 16px;
    }
  }
}

.dark {
  .contract-info {
    &__container {
      background-color: @dt-dark-bg !important;
      border: 1px solid @dt-dark-primary !important;
    }
  }
}
</style>
